import { useCallback, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import LoadingButton from "@mui/lab/LoadingButton";
import { alpha, useTheme } from "@mui/material/styles";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";

import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
// Remove this import: import { bgGradient } from "../../theme/css";

import Logo from "../../components/logo";
import Iconify from "../../components/iconify";
import AuroraBackground from "../../components/AuroraBackground"; // Add this import
import useAuth from "src/hooks/useAuth";
import toast from "react-hot-toast";
import { GOOGLE_AUTH_URL } from "src/config/api.config";
import { useGoogleLogin } from "@react-oauth/google";
import { doPost } from "src/utils/apiCallers";
import { useNavigate } from "react-router-dom";
import { analytics } from "src/config/firebase.config";
import { logEvent } from "firebase/analytics";
import { useMediaQuery } from "@mui/material";

// ----------------------------------------------------------------------

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
});

export default function LoginView() {
  const theme = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = useCallback(
    async (formData, setSubmitting, reset) => {
      try {
        setSubmitting(true);

        await login(formData.email, formData.password);
        logEvent(analytics, "button_click", { button_name: "Sign In" });

        reset();
      } catch (error) {
        console.error("Error logging in:", error);
        toast.error("Failed to login. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [login]
  );

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      onSubmit(values, setSubmitting, resetForm);
    },
  });

  const renderForm = (
    <form onSubmit={formik.handleSubmit}>
      <Stack spacing={3}>
        <TextField
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          name="email"
          label="Email address"
          helperText={formik.touched.email && formik.errors.email}
          sx={{
            "& .MuiOutlinedInput-root": {
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(theme.palette.background.default, 0.3),
              "& fieldset": {
                borderColor: alpha(theme.palette.common.white, 0.2),
              },
              "&:hover fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        <TextField
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          sx={{
            "& .MuiOutlinedInput-root": {
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(theme.palette.background.default, 0.3),
              "& fieldset": {
                borderColor: alpha(theme.palette.common.white, 0.2),
              },
              "&:hover fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.7),
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.action.hover, 0.1),
                    },
                  }}
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        sx={{ my: 3 }}
      >
        <Link
          variant="subtitle2"
          underline="hover"
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/reset-password")}
        >
          Forgot password?
        </Link>
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        color="primary"
        disabled={formik.isSubmitting}
      >
        {formik.isSubmitting ? <CircularProgress size={24} /> : "Login"}
      </LoadingButton>
      <Typography
        variant="subtitle2"
        sx={{
          display: "flex",
          justifyContent: "center",
          opacity: 0.7,
          marginTop: 3,
        }}
      >
        By Signing in you agree to&nbsp;
        <Link href="/terms-of-use" target="_blank" sx={{ cursor: "pointer" }}>
          <Typography
            underline="hover"
            sx={{
              display: "inline",
              color: "secondary.main",
            }}
          >
            Terms & Condition
          </Typography>
        </Link>
      </Typography>
    </form>
  );

  const handleGoogleLoginSuccess = async (tokenResponse) => {
    try {
      const result = await doPost(
        `${GOOGLE_AUTH_URL}/profile`,
        JSON.stringify({ accessToken: tokenResponse.access_token }),
        null,
        { "Content-Type": "application/json" },
        null
      );

      console.log("Google OAuth Success:", result);

      const { success, message, data } = result;
      const userName = data.name || "User";
      await login(null, null, success, message, data);
      toast.success(`Login Success, Welcome ${userName}`);
    } catch (error) {
      console.error("Google OAuth Error:", error);
      toast.error("Failed to authenticate with Google.");
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleLoginSuccess,
    onError: (error) => {
      console.error("Google OAuth Error:", error);
      toast.error("Failed to sign in with Google.");
    },
  });

  const handleFacebookLoginSuccess = async (response) => {
    try {
      console.log("Facebook Login Success:", response);

      const res = await fetch(
        "https://api2.riple.org/v1/auth/facebook/profile",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accessToken: response.accessToken }),
        }
      );

      const data = await res.json();
      console.log("Response", data);
      if (data?.data) {
        const userName = data.data.name || "User";
        console.log(`Login Success, Welcome ${userName}`);
        toast.success(`Login Success, Welcome ${userName}`);
      } else {
        throw new Error("Invalid response data from the server.");
      }
    } catch (error) {
      console.error("Facebook OAuth Error:", error);
      toast.error("Failed to authenticate with Facebook. Please try again.");
    }
  };

  const handleFacebookLoginFailure = (error) => {
    console.error("Facebook OAuth Error:", error);
    alert("Failed to sign in with Facebook.");
  };

  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  return (
    <AuroraBackground showRadialGradient={true}>
      <Logo
        sx={{
          position: "fixed",
          top: { xs: 16, md: 24 },
          left: { xs: 16, md: 24 },
          zIndex: 10, // Ensure logo stays on top
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-evenly"
        sx={{
          height: "100%",
          width: "100%",
          position: "relative",
          zIndex: 1, // Ensure content stays above
        }}
      >
        <Card
          sx={{
            p: 5,
            width: 1,
            maxWidth: 500,
            // Glassmorphism effect
            backdropFilter: "blur(15px)",
            backgroundColor:
              isSmUp &&
              (theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.1)
                : alpha(theme.palette.background.paper, 0.2)),
            border:
              isSmUp && `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
            borderRadius: isSmUp ? 2 : 0,
            position: "relative",
            overflow: "hidden",
            // Enhanced shadow with multiple layers
            boxShadow:
              isSmUp &&
              `
              0 8px 32px ${alpha(theme.palette.common.black, 0.1)},
              0 4px 16px ${alpha(theme.palette.common.black, 0.05)},
              inset 0 2px 2px ${alpha(theme.palette.common.white, 0.15)}
            `,
            // Subtle gradient overlay
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "2px",
              background: `linear-gradient(90deg, 
                transparent 0%, 
                ${alpha(theme.palette.primary.light, 0.4)} 70%, 
                transparent 100%
              )`,
            },
            // Additional frosted glass effect
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                isSmUp &&
                (theme.palette.mode === "dark"
                  ? `linear-gradient(135deg,
                    ${alpha(theme.palette.primary.main, 0.02)} 0%,
                    ${alpha(theme.palette.background.paper, 0.01)} 100%
                  )`
                  : `linear-gradient(135deg,
                    ${alpha(theme.palette.primary.main, 0.03)} 0%,
                    ${alpha(theme.palette.background.paper, 0.04)} 100%
                  )`),
              pointerEvents: "none",
              zIndex: -1,
            },
          }}
        >
          <Typography variant="h3">Sign in to Riple</Typography>

          <Typography variant="body1" sx={{ mt: 2, mb: 5 }}>
            Don't have an account?
            <Link
              color="inherit"
              variant="subtitle2"
              sx={{ ml: 0.5 }}
              href="/signup"
            >
              Get started
            </Link>
          </Typography>

          {renderForm}
        </Card>
      </Stack>
    </AuroraBackground>
  );
}
